import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

function CaseDetail() {
  const { caseNumber } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyForm, setReplyForm] = useState({
    senderEmail: '',
    senderName: '',
    subject: '',
    content: ''
  });
  const [submittingReply, setSubmittingReply] = useState(false);

  useEffect(() => {
    fetchCaseDetails();
    fetchReplies();
  }, [caseNumber]);

  const fetchCaseDetails = async () => {
    try {
      setLoading(true);
      const API_BASE_URL = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API_BASE_URL}/cases/case/${caseNumber}`);
      
      if (!response.ok) {
        throw new Error(`Case not found: ${response.status}`);
      }
      
      const data = await response.json();
      setCaseData(data);
    } catch (err) {
      console.error("Error fetching case details:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchReplies = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API_BASE_URL}/cases/${caseNumber}/replies`);
      
      if (response.ok) {
        const data = await response.json();
        setReplies(data.replies || []);
      }
    } catch (err) {
      console.error("Error fetching replies:", err);
    }
  };

  const handleReplyChange = (e) => {
    setReplyForm({ ...replyForm, [e.target.name]: e.target.value });
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    
    if (!replyForm.senderEmail || !replyForm.subject || !replyForm.content) {
      alert("Please fill in all required fields");
      return;
    }

    setSubmittingReply(true);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API_BASE_URL}/cases/${caseNumber}/replies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          caseNumber,
          ...replyForm
        })
      });

      if (!response.ok) {
        throw new Error("Failed to add reply");
      }

      // Reset form and refresh replies
      setReplyForm({ senderEmail: '', senderName: '', subject: '', content: '' });
      setShowReplyForm(false);
      await fetchReplies();
      alert("Reply added successfully!");
    } catch (err) {
      console.error("Error adding reply:", err);
      alert("Error adding reply: " + err.message);
    } finally {
      setSubmittingReply(false);
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "malware": return "#dc3545";
      case "phishing": return "#fd7e14";
      case "ncc": return "#6f42c1";
      case "spy-app": return "#e83e8c";
      default: return "#6c757d";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending": return "#ffc107";
      case "Notice Sent": return "#007bff";
      case "Under Review": return "#17a2b8";
      case "Resolved": return "#28a745";
      default: return "#6c757d";
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Content copied to clipboard!");
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: "100vh", 
        backgroundColor: "#f8f9fa", 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center" 
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "2rem", marginBottom: "1rem" }}>🔍</div>
          <h3>Loading Case Details...</h3>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "#f8f9fa", padding: "2rem" }}>
        <div style={{ 
          maxWidth: "600px", 
          margin: "0 auto", 
          backgroundColor: "white", 
          padding: "2rem", 
          borderRadius: "8px",
          textAlign: "center"
        }}>
          <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>❌</div>
          <h2 style={{ color: "#dc3545", marginBottom: "1rem" }}>Case Not Found</h2>
          <p style={{ color: "#666", marginBottom: "2rem" }}>
            Case number "{caseNumber}" could not be found in the database.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              padding: "0.75rem 1.5rem",
              borderRadius: "4px",
              fontSize: "1rem",
              cursor: "pointer"
            }}
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      {/* Header */}
      <div style={{ 
        backgroundColor: "white", 
        padding: "1rem 2rem", 
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        borderBottom: "1px solid #e9ecef"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h1 style={{ margin: 0, fontSize: "1.5rem", color: "#333" }}>
              Case Details - #{caseData.caseNumber}
            </h1>
            <p style={{ margin: "0.25rem 0 0 0", color: "#666", fontSize: "0.9rem" }}>
              Digital Safe India - Case Management System
            </p>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              backgroundColor: "#6c757d",
              color: "white",
              border: "none",
              padding: "0.5rem 1rem",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>

      <div style={{ padding: "2rem" }}>
        <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
          {/* Case Overview Card */}
          <div style={{ 
            backgroundColor: "white", 
            borderRadius: "8px", 
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            marginBottom: "2rem",
            overflow: "hidden"
          }}>
            <div style={{ 
              backgroundColor: "#f8f9fa", 
              padding: "1rem 1.5rem", 
              borderBottom: "1px solid #e9ecef"
            }}>
              <h2 style={{ margin: 0, color: "#333" }}>Case Overview</h2>
            </div>
            
            <div style={{ padding: "1.5rem" }}>
              <div style={{ 
                display: "grid", 
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
                gap: "1.5rem",
                marginBottom: "1.5rem"
              }}>
                <div>
                  <label style={{ fontSize: "0.875rem", fontWeight: "bold", color: "#666", display: "block", marginBottom: "0.5rem" }}>
                    Case Number
                  </label>
                  <div style={{ 
                    fontSize: "1.25rem", 
                    fontWeight: "bold", 
                    color: "#007bff",
                    padding: "0.5rem",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "4px"
                  }}>
                    #{caseData.caseNumber}
                  </div>
                </div>
                
                <div>
                  <label style={{ fontSize: "0.875rem", fontWeight: "bold", color: "#666", display: "block", marginBottom: "0.5rem" }}>
                    Case Type
                  </label>
                  <div style={{ 
                    display: "inline-block",
                    padding: "0.5rem 1rem", 
                    borderRadius: "20px", 
                    fontSize: "0.875rem", 
                    fontWeight: "500",
                    textTransform: "capitalize",
                    color: "white",
                    backgroundColor: getTypeColor(caseData.type)
                  }}>
                    {caseData.type}
                  </div>
                </div>
                
                <div>
                  <label style={{ fontSize: "0.875rem", fontWeight: "bold", color: "#666", display: "block", marginBottom: "0.5rem" }}>
                    Status
                  </label>
                  <div style={{ 
                    display: "inline-block",
                    padding: "0.5rem 1rem", 
                    borderRadius: "20px", 
                    fontSize: "0.875rem", 
                    fontWeight: "500",
                    color: "white",
                    backgroundColor: getStatusColor(caseData.status)
                  }}>
                    {caseData.status}
                  </div>
                </div>
              </div>
              
              <div style={{ 
                display: "grid", 
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", 
                gap: "1.5rem"
              }}>
                <div>
                  <label style={{ fontSize: "0.875rem", fontWeight: "bold", color: "#666", display: "block", marginBottom: "0.5rem" }}>
                    Reported URL
                  </label>
                  <div style={{ 
                    padding: "0.75rem",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "4px",
                    border: "1px solid #e9ecef",
                    wordBreak: "break-all"
                  }}>
                    <a 
                      href={caseData.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: "#007bff", textDecoration: "none" }}
                    >
                      {caseData.url}
                    </a>
                  </div>
                </div>
                
                <div>
                  <label style={{ fontSize: "0.875rem", fontWeight: "bold", color: "#666", display: "block", marginBottom: "0.5rem" }}>
                    Case Created
                  </label>
                  <div style={{ 
                    padding: "0.75rem",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "4px",
                    border: "1px solid #e9ecef"
                  }}>
                    {new Date(caseData.createdAt).toLocaleString()}
                  </div>
                </div>
                
                {caseData.emailSentAt && (
                  <div>
                    <label style={{ fontSize: "0.875rem", fontWeight: "bold", color: "#666", display: "block", marginBottom: "0.5rem" }}>
                      Email Sent
                    </label>
                    <div style={{ 
                      padding: "0.75rem",
                      backgroundColor: "#d4edda",
                      borderRadius: "4px",
                      border: "1px solid #c3e6cb",
                      color: "#155724"
                    }}>
                      ✅ {new Date(caseData.emailSentAt).toLocaleString()}
                    </div>
                  </div>
                )}
                
                {caseData.emailRecipient && (
                  <div>
                    <label style={{ fontSize: "0.875rem", fontWeight: "bold", color: "#666", display: "block", marginBottom: "0.5rem" }}>
                      Email Recipient
                    </label>
                    <div style={{ 
                      padding: "0.75rem",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "4px",
                      border: "1px solid #e9ecef"
                    }}>
                      📧 {caseData.emailRecipient}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* DMCA Content Card */}
          {caseData.emailContent && (
            <div style={{ 
              backgroundColor: "white", 
              borderRadius: "8px", 
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              overflow: "hidden",
              marginBottom: "2rem"
            }}>
              <div style={{ 
                backgroundColor: "#f8f9fa", 
                padding: "1rem 1.5rem", 
                borderBottom: "1px solid #e9ecef",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}>
                <h2 style={{ margin: 0, color: "#333" }}>📤 DMCA Notice Sent</h2>
                <button
                  onClick={() => copyToClipboard(caseData.emailContent)}
                  style={{
                    backgroundColor: "#007bff",
                    color: "white",
                    border: "none",
                    padding: "0.5rem 1rem",
                    borderRadius: "4px",
                    fontSize: "0.875rem",
                    cursor: "pointer"
                  }}
                >
                  📋 Copy Content
                </button>
              </div>
              
              <div style={{ padding: "1.5rem" }}>
                <div style={{ 
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #e9ecef",
                  borderRadius: "6px",
                  overflow: "hidden"
                }}>
                  {/* Email Header Simulation */}
                  <div style={{ 
                    backgroundColor: "#e9ecef", 
                    padding: "1rem", 
                    borderBottom: "1px solid #dee2e6",
                    fontSize: "0.875rem"
                  }}>
                    <div style={{ marginBottom: "0.25rem" }}>
                      <strong>To:</strong> {caseData.emailRecipient || "amitpnd555@gmail.com"}
                    </div>
                    <div style={{ marginBottom: "0.25rem" }}>
                      <strong>Subject:</strong> DMCA Takedown Request - {caseData.type.charAt(0).toUpperCase() + caseData.type.slice(1)} [Case #{caseData.caseNumber}]
                    </div>
                    <div style={{ marginBottom: "0.25rem" }}>
                      <strong>From:</strong> Digital Safe India &lt;helpvictims9@gmail.com&gt;
                    </div>
                    <div>
                      <strong>Date:</strong> {caseData.emailSentAt ? new Date(caseData.emailSentAt).toLocaleString() : "Not sent"}
                    </div>
                  </div>
                  
                  {/* Email Content */}
                  <div style={{ 
                    padding: "1.5rem", 
                    backgroundColor: "white",
                    fontSize: "0.9rem",
                    lineHeight: "1.6",
                    whiteSpace: "pre-wrap",
                    fontFamily: "Georgia, serif",
                    maxHeight: "500px",
                    overflowY: "auto",
                    color: "#000"
                  }}>
                    {caseData.emailContent}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Email Conversation Thread */}
          <div style={{ 
            backgroundColor: "white", 
            borderRadius: "8px", 
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            overflow: "hidden"
          }}>
            <div style={{ 
              backgroundColor: "#f8f9fa", 
              padding: "1rem 1.5rem", 
              borderBottom: "1px solid #e9ecef",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}>
              <h2 style={{ margin: 0, color: "#333" }}>
                💬 Email Replies ({replies.length})
              </h2>
              <button
                onClick={() => setShowReplyForm(!showReplyForm)}
                style={{
                  backgroundColor: showReplyForm ? "#dc3545" : "#28a745",
                  color: "white",
                  border: "none",
                  padding: "0.5rem 1rem",
                  borderRadius: "4px",
                  fontSize: "0.875rem",
                  cursor: "pointer"
                }}
              >
                {showReplyForm ? "✕ Cancel" : "➕ Add Reply"}
              </button>
            </div>

            <div style={{ padding: "1.5rem" }}>
              {/* Add Reply Form */}
              {showReplyForm && (
                <div style={{
                  backgroundColor: "#f0f7ff",
                  border: "1px solid #b3d9ff",
                  borderRadius: "6px",
                  padding: "1.5rem",
                  marginBottom: "1.5rem"
                }}>
                  <h3 style={{ margin: "0 0 1rem 0", color: "#0066cc" }}>Add Email Reply</h3>
                  <form onSubmit={handleSubmitReply}>
                    <div style={{ marginBottom: "1rem" }}>
                      <label style={{ display: "block", fontWeight: "bold", marginBottom: "0.5rem", color: "#333" }}>
                        Sender Email *
                      </label>
                      <input
                        type="email"
                        name="senderEmail"
                        value={replyForm.senderEmail}
                        onChange={handleReplyChange}
                        placeholder="e.g., noreply@platform.com"
                        required
                        style={{
                          width: "100%",
                          padding: "0.75rem",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          fontSize: "1rem",
                          boxSizing: "border-box"
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: "1rem" }}>
                      <label style={{ display: "block", fontWeight: "bold", marginBottom: "0.5rem", color: "#333" }}>
                        Sender Name
                      </label>
                      <input
                        type="text"
                        name="senderName"
                        value={replyForm.senderName}
                        onChange={handleReplyChange}
                        placeholder="e.g., Platform Support Team"
                        style={{
                          width: "100%",
                          padding: "0.75rem",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          fontSize: "1rem",
                          boxSizing: "border-box"
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: "1rem" }}>
                      <label style={{ display: "block", fontWeight: "bold", marginBottom: "0.5rem", color: "#333" }}>
                        Subject *
                      </label>
                      <input
                        type="text"
                        name="subject"
                        value={replyForm.subject}
                        onChange={handleReplyChange}
                        placeholder="e.g., Re: DMCA Takedown Request - Phishing [Case #96041]"
                        required
                        style={{
                          width: "100%",
                          padding: "0.75rem",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          fontSize: "1rem",
                          boxSizing: "border-box"
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: "1rem" }}>
                      <label style={{ display: "block", fontWeight: "bold", marginBottom: "0.5rem", color: "#333" }}>
                        Reply Content *
                      </label>
                      <textarea
                        name="content"
                        value={replyForm.content}
                        onChange={handleReplyChange}
                        placeholder="Paste the email reply content here..."
                        required
                        rows="8"
                        style={{
                          width: "100%",
                          padding: "0.75rem",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          fontSize: "1rem",
                          fontFamily: "Georgia, serif",
                          boxSizing: "border-box"
                        }}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submittingReply}
                      style={{
                        backgroundColor: "#0066cc",
                        color: "white",
                        border: "none",
                        padding: "0.75rem 1.5rem",
                        borderRadius: "4px",
                        fontSize: "1rem",
                        cursor: "pointer",
                        opacity: submittingReply ? 0.7 : 1
                      }}
                    >
                      {submittingReply ? "Submitting..." : "✓ Add Reply"}
                    </button>
                  </form>
                </div>
              )}

              {/* Replies List */}
              {replies.length === 0 ? (
                <div style={{
                  textAlign: "center",
                  padding: "2rem",
                  color: "#666"
                }}>
                  <p style={{ fontSize: "1rem" }}>No replies yet</p>
                  <p style={{ fontSize: "0.875rem", marginTop: "0.5rem" }}>
                    Email replies will appear here once received
                  </p>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                  {replies.map((reply, index) => (
                    <div
                      key={reply._id || index}
                      style={{
                        backgroundColor: "#fff5e6",
                        border: "1px solid #ffb3cc",
                        borderRadius: "6px",
                        overflow: "hidden"
                      }}
                    >
                      {/* Reply Header */}
                      <div style={{
                        backgroundColor: "#ffe6f0",
                        padding: "1rem",
                        borderBottom: "1px solid #ffb3cc",
                        fontSize: "0.875rem"
                      }}>
                        <div style={{ marginBottom: "0.25rem" }}>
                          <strong>From:</strong> {reply.senderName} &lt;{reply.senderEmail}&gt;
                        </div>
                        <div style={{ marginBottom: "0.25rem" }}>
                          <strong>Subject:</strong> {reply.subject}
                        </div>
                        <div>
                          <strong>Date:</strong> {new Date(reply.receivedAt).toLocaleString()}
                        </div>
                      </div>

                      {/* Reply Content */}
                      <div style={{
                        padding: "1.5rem",
                        backgroundColor: "white",
                        fontSize: "0.9rem",
                        lineHeight: "1.6",
                        whiteSpace: "pre-wrap",
                        fontFamily: "Georgia, serif",
                        maxHeight: "400px",
                        overflowY: "auto",
                        color: "#dc3545"
                      }}>
                        {reply.content}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CaseDetail;
